import { AppError } from '../utils/appErrors';
import { markOwnedReadCancellation, requestOperation, REQUEST_OPERATION_HEADER, REQUEST_VERSION_CONFLICT_HEADER, reportActionFailure, reportRejectedAction } from '../lib/actionFailureSignals';
import {
  getAuthTransportExpectation,
  reportSessionFailure,
} from './sessionSignals';

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

function requestHeaders(input: RequestInfo | URL, init?: RequestInit): Headers {
  const headers = new Headers(input instanceof Request ? input.headers : undefined);
  new Headers(init?.headers).forEach((value, key) => headers.set(key, value));
  return headers;
}

type FetchImplementation = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

/**
 * Builds the transport used by Supabase data clients. Auth requests are always
 * exempt because they must be able to refresh or reject the stored session.
 * Every other request fails closed while the current route requires a staff
 * session, so the SDK cannot silently substitute the project's anonymous key.
 */
export function createGuardedSupabaseFetch(
  anonKey: string | undefined,
  fetchImplementation: FetchImplementation = (input, init) => globalThis.fetch(input, init),
): FetchImplementation {
  return async (input, init) => {
    const url = requestUrl(input);
    const isAuthRequest = /\/auth\/v1(?:\/|$|\?)/.test(url);
    const expectation = getAuthTransportExpectation();

    if (!isAuthRequest && expectation.mode === 'blocked') {
      reportSessionFailure({
        reason: 'missing_bearer_token',
        source: 'supabase',
      });
      throw new AppError(
        'session_expired',
        'Your session expired. Sign in again to continue.',
        { context: 'supabase_transport_transition' },
      );
    }

    if (!isAuthRequest && expectation.mode === 'session') {
      const authorization = requestHeaders(input, init).get('authorization') ?? '';
      const bearerToken = authorization.match(/^Bearer\s+(.+)$/i)?.[1]?.trim() ?? '';
      if (
        !bearerToken
        || bearerToken === anonKey
        || !expectation.accessToken
        || bearerToken !== expectation.accessToken
      ) {
        reportSessionFailure({
          reason: 'missing_bearer_token',
          source: 'supabase',
        });
        throw new AppError(
          'session_expired',
          'Your session expired. Sign in again to continue.',
          { context: 'supabase_transport' },
        );
      }
    }

    const operation = requestOperation(input, init);
    const isDiagnostic = /\/rest\/v1\/(?:perf_telemetry|client_errors)(?:[/?]|$)/.test(url);
    const isWrite = !isAuthRequest && !isDiagnostic && operation === 'mutation';
    const headers = requestHeaders(input, init);
    const ownsVersionConflict = headers.get(REQUEST_VERSION_CONFLICT_HEADER) === 'caller'
      && new URL(url, 'http://localhost').searchParams.get('updated_at')?.startsWith('eq.');
    headers.delete(REQUEST_OPERATION_HEADER);
    headers.delete(REQUEST_VERSION_CONFLICT_HEADER);
    const signal = init?.signal ?? (input instanceof Request ? input.signal : undefined);
    let response: Response;
    try {
      response = await fetchImplementation(input, { ...init, headers });
    } catch (error) {
      if (operation === 'read' && signal?.aborted && signal.reason?.name === 'AbortError'
        && error && typeof error === 'object' && 'name' in error && error.name === 'AbortError') {
        markOwnedReadCancellation(error);
        // PostgREST converts thrown fetch errors to plain objects. Preserve the
        // caller's cancellation ownership through that public SDK boundary.
        return new Response(JSON.stringify({ code: 'PORTAL_READ_CANCELLED', message: 'Read cancelled by its caller.' }),
          { status: 499, headers: { 'content-type': 'application/json' } });
      }
      if (isWrite) reportActionFailure(error);
      throw error;
    }
    if (isWrite && !response.ok) await reportRejectedAction(response, { handledVersionConflict: ownsVersionConflict });
    if (isWrite && response.ok && typeof window !== 'undefined') {
      window.dispatchEvent(new Event('portal:write-committed'));
    }
    if (!isAuthRequest && expectation.mode === 'session') {
      if (response.status === 401) {
        reportSessionFailure({
          reason: 'protected_request_unauthorized',
          source: 'supabase',
          status: response.status,
        });
      } else if (response.status === 403) {
        reportSessionFailure({
          reason: 'protected_request_forbidden',
          source: 'supabase',
          status: response.status,
        });
      }
    }
    return response;
  };
}
